import React, { useState, useEffect } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { financeiroApi } from '../../../services/api';
import type { FluxoCaixaData } from '../../../types';

const FluxoCaixa: React.FC = () => {
  const [data, setData] = useState<FluxoCaixaData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState(30);

  useEffect(() => {
    loadFluxoCaixa();
  }, [periodo]);

  const loadFluxoCaixa = async () => {
    try {
      setLoading(true);
      const response = await financeiroApi.getFluxoCaixa(periodo);
      setData(response);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar fluxo de caixa');
      console.error('Error loading fluxo de caixa:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | undefined) => {
    if (value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR').format(new Date(dateString));
  };

  if (loading) {
    return (
    <div className="space-y-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-8">
        <h1 className="text-base font-bold text-gray-900">Fluxo de Caixa</h1>
        <p className="mt-0.5 text-xs text-gray-700">
          Acompanhamento do fluxo de caixa por período
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <ExclamationCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Filtro de Período */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Período:</label>
          <div className="flex space-x-2">
            {[7, 15, 30, 60, 90].map((dias) => (
              <button
                key={dias}
                onClick={() => setPeriodo(dias)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  periodo === dias
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {dias} dias
              </button>
            ))}
          </div>
        </div>
      </div>

      {data && (
        <>
          {/* Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-500">Saldo Inicial</h3>
              <p className="mt-2 text-2xl font-semibold text-gray-900">
                {formatCurrency(data.saldo_inicial)}
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-500">Total Receitas</h3>
              <p className="mt-2 text-2xl font-semibold text-green-600">
                {formatCurrency(data.fluxo.reduce((acc, f) => acc + f.receitas, 0))}
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-500">Total Despesas</h3>
              <p className="mt-2 text-2xl font-semibold text-red-600">
                {formatCurrency(data.fluxo.reduce((acc, f) => acc + f.despesas, 0))}
              </p>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <h3 className="text-sm font-medium text-gray-500">Saldo Final</h3>
              <p
                className={`mt-2 text-2xl font-semibold ${
                  (data.fluxo.length > 0 ? data.fluxo[data.fluxo.length - 1].saldo_acumulado : data.saldo_inicial) >= 0 ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {formatCurrency(data.fluxo.length > 0 ? data.fluxo[data.fluxo.length - 1].saldo_acumulado : data.saldo_inicial)}
              </p>
            </div>
          </div>

          {/* Tabela de Fluxo Diário */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-sm font-bold text-gray-900">Fluxo Diário</h2>
            </div>
            <div className="overflow-x-auto">
              {data.fluxo && data.fluxo.length > 0 ? (
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Receitas
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Despesas
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Saldo do Dia
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Saldo Acumulado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {data.fluxo.map((item, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm font-medium text-gray-900">
                            {formatDate(item.data)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-green-600">
                            +{formatCurrency(item.receitas)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span className="text-sm font-medium text-red-600">
                            -{formatCurrency(item.despesas)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span
                            className={`text-sm font-medium ${
                              item.saldo_dia >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatCurrency(item.saldo_dia)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <span
                            className={`text-sm font-semibold ${
                              item.saldo_acumulado >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}
                          >
                            {formatCurrency(item.saldo_acumulado)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-semibold">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-900">TOTAL</td>
                      <td className="px-4 py-3 text-right text-sm text-green-600">
                        {formatCurrency(data.fluxo.reduce((acc, f) => acc + f.receitas, 0))}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-red-600">
                        {formatCurrency(data.fluxo.reduce((acc, f) => acc + f.despesas, 0))}
                      </td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className={`text-sm font-semibold ${
                            (data.fluxo.length > 0 ? data.fluxo[data.fluxo.length - 1].saldo_acumulado : data.saldo_inicial) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                        >
                          {formatCurrency(data.fluxo.length > 0 ? data.fluxo[data.fluxo.length - 1].saldo_acumulado : data.saldo_inicial)}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              ) : (
                <div className="text-center py-12">
                  <ExclamationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    Nenhum dado disponível
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Não há movimentações no período selecionado.
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FluxoCaixa;
